import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { BaseFilterCellComponent, ColumnComponent, FilterService } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { EngineTranslationService } from '../../../../../core/engine-translations/services/translation.service';
import { Precision } from '../../../../ui-components/components/duration/duration.service';
import { FilterOperator } from '../../../models/filter-operator.model';
import { TimeFilterMenuInputComponent } from './time-filter-menu-input/time-filter-menu-input.component';

@Component({
  selector: 'app-time-filter',
  templateUrl: './time-filter.component.html',
  styleUrls: ['./time-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TimeFilterMenuInputComponent, DropDownListComponent],
})
export class TimeFilterComponent extends BaseFilterCellComponent implements OnInit, AfterViewInit {
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _translationService = inject(EngineTranslationService);

  static readonly OR_NAME = 'kendo.grid.filterOrLogic';
  static readonly AND_NAME = 'kendo.grid.filterAndLogic';

  @Input() column!: ColumnComponent;
  @Input() override filter!: CompositeFilterDescriptor;
  @Input() override filterService!: FilterService;
  readonly maxPrecision = input<Precision>('hours');

  firstFilterDescriptor: FilterDescriptor;
  secondFilterDescriptor: FilterDescriptor;
  logicOperators: Array<FilterOperator>;

  logicOperator: FilterOperator;

  constructor() {
    const filterService = inject(FilterService);

    super(filterService);
  }

  ngOnInit() {
    this.addLogicalOperatorsWithTranslation();
  }

  ngAfterViewInit(): void {
    const currentColumnFilter = <FilterDescriptor[]>(
      this.filter.filters.filter((filter: FilterDescriptor) => filter.field === this.column.field)
    );

    if (this.filter.logic) this.logicOperator = this.logicOperators.find((o) => o.value == this.filter.logic);

    if (currentColumnFilter) {
      if (currentColumnFilter.length > 0) this.firstFilterDescriptor = currentColumnFilter[0];

      if (currentColumnFilter.length > 1) this.secondFilterDescriptor = currentColumnFilter[1];

      this._changeDetectorRef.markForCheck();
    }

    this._changeDetectorRef.markForCheck();
  }

  get logic() {
    return this.logicOperator.value == 'and' ? 'and' : 'or';
  }

  onFirstValueChange(value: FilterDescriptor) {
    this.firstFilterDescriptor = value;

    let filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];

    if (value === null || value === undefined) {
      filters = [this.secondFilterDescriptor];
    } else {
      filters = [this.firstFilterDescriptor, this.secondFilterDescriptor];
    }

    this.filterService.filter({
      filters: filters.filter((n) => n),
      logic: this.logic,
    });
  }

  onSecondValueChange(value: FilterDescriptor): void {
    this.secondFilterDescriptor = value;

    let filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];

    if (value === null || value === undefined) {
      filters = [this.firstFilterDescriptor];
    } else {
      filters = [this.secondFilterDescriptor, this.firstFilterDescriptor];
    }

    this.filterService.filter({
      filters: filters.filter((n) => n),
      logic: this.logic,
    });
  }

  onLogicOperatorChange(value: any) {
    this.logicOperator = value;

    this.filterService.filter({
      filters: [this.secondFilterDescriptor, this.firstFilterDescriptor].filter((n) => n),
      logic: this.logic,
    });
  }

  private addLogicalOperatorsWithTranslation() {
    const andTranslation = this._translationService.translateInstantly(TimeFilterComponent.AND_NAME);
    const orTranslation = this._translationService.translateInstantly(TimeFilterComponent.OR_NAME);

    this.logicOperators = [
      { text: andTranslation, value: 'and' },
      { text: orTranslation, value: 'or' },
    ];
  }
}
